import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import {
  SUPPORT_ATTACHMENT_MAX_COUNT,
  appendSupportImageFiles,
  captureScreen,
  captureVisiblePage,
  readClipboardImage,
  readClipboardImageFromEvent,
  validateSupportImageFileList,
} from '@wapharma/shared';
import {
  IconArrowLeft,
  IconArrowUpRight,
  IconClipboard,
  IconDeviceDesktop,
  IconPaperclip,
  IconPhoto,
  IconSend2,
} from '@tabler/icons-react';
import {
  Avatar,
  EmojiPicker,
  EmptyState,
  ImageAttachmentGrid,
  IconButton,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextArea,
  Tooltip,
  useDebouncedValue,
  WP_ICON_NAV,
  type ConversationMessageItem,
} from '@wapharma/design-system';
import { getToken } from '../auth';
import { getDisplayError } from '../apiErrors';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import type { ConversationView } from '../hooks/useConversation';
import {
  detectReferenceMention,
  fetchMessageReferenceSuggestions,
  getMessageReferenceEntityKind,
  type MessageReferenceSuggestion,
} from '../lib/messageReferenceSearch';
import { getEntityHref, getEntityLabel } from '../lib/entityRegistry';
import { EntityReferenceChip } from './ComposerReferenceChip';
import { EntityTypeMark } from './EntityTypeMark';
import { getSelectableMentionEntries, MessageReferenceMentionMenu } from './MessageReferenceMentionMenu';
import styles from './MessagesConversationPane.module.css';

type LocalDraftReference = {
  key: string;
  refType: string;
  refId: string;
  label: string | null;
};

type DraftReferenceSeed = {
  refType: string;
  refId: string;
  label?: string | null;
};

function toLocalDraftReference(seed: DraftReferenceSeed): LocalDraftReference {
  return {
    key: `${seed.refType}:${seed.refId}`,
    refType: seed.refType,
    refId: seed.refId,
    label: seed.label?.trim() || null,
  };
}

function localDraftReferenceKey(refType: string, refId: string): string {
  return `${refType}:${refId}`;
}

type SendInput = {
  body: string;
  isInternal: boolean;
  channel?: 'EMAIL' | 'WHATSAPP';
  subject?: string | null;
  files: File[];
  references?: Array<{ refType: string; refId: string; label?: string | null }>;
};

type MessagesConversationPaneProps = {
  conversation: ConversationView | null;
  loading: boolean;
  title: string;
  subtitle?: string | null;
  /** When set, show toned type mark under title. */
  entityType?: string | null;
  avatarUrl?: string | null;
  awaitingStart?: boolean;
  initialDraftReferences?: DraftReferenceSeed[];
  onSend: (input: SendInput) => Promise<void>;
  onAddReference?: (input: { refType: string; refId: string; label?: string | null }) => Promise<void>;
  onRemoveReference?: (referenceId: string) => Promise<void>;
  onBack?: () => void;
  onOpenContext?: () => void;
  compact?: boolean;
};

const GROUP_GAP_MS = 10 * 60 * 1000;

function messageTone(
  message: ConversationMessageItem,
  viewerUserId?: string | null,
): 'inbound' | 'outbound' | 'internal' {
  if (message.isInternal) return 'internal';
  if (viewerUserId && message.authorUserId) {
    return message.authorUserId === viewerUserId ? 'outbound' : 'inbound';
  }
  if (message.direction === 'INTERNAL') return 'internal';
  if (message.direction === 'INBOUND') return 'inbound';
  return 'outbound';
}

function messageLabel(message: ConversationMessageItem, viewerUserId?: string | null): string {
  if (message.isInternal) return 'Note interne';
  if (viewerUserId && message.authorUserId) {
    if (message.authorUserId === viewerUserId) return 'Vous';
    return message.authorFirstName?.trim() || 'Collaborateur';
  }
  if (message.direction === 'INBOUND') return message.authorFirstName?.trim() || 'Client';
  return message.authorFirstName?.trim() || 'Équipe';
}

export function MessagesConversationPane({
  conversation,
  loading,
  title,
  subtitle,
  entityType,
  avatarUrl,
  awaitingStart = false,
  initialDraftReferences,
  onSend,
  onAddReference,
  onRemoveReference,
  onBack,
  onOpenContext,
  compact = false,
}: MessagesConversationPaneProps) {
  const { user } = useAuth();
  const viewerUserId = user?.id ?? null;
  const token = getToken();
  const { showToast } = useToast();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [pending, setPending] = useState(false);
  const [busyAction, setBusyAction] = useState<'paste' | 'screen' | 'page' | null>(null);
  const [caret, setCaret] = useState(0);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const [mentionSuggestions, setMentionSuggestions] = useState<MessageReferenceSuggestion[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [removingReferenceId, setRemovingReferenceId] = useState<string | null>(null);
  const [localDraftReferences, setLocalDraftReferences] = useState<LocalDraftReference[]>(() =>
    (initialDraftReferences ?? []).map(toLocalDraftReference),
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const previews = useMemo(
    () =>
      files.map((file, index) => ({
        id: `${file.name}-${index}`,
        signedUrl: URL.createObjectURL(file),
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      })),
    [files],
  );

  useEffect(
    () => () => {
      previews.forEach((item) => URL.revokeObjectURL(item.signedUrl));
    },
    [previews],
  );

  useEffect(() => {
    setLocalDraftReferences((initialDraftReferences ?? []).map(toLocalDraftReference));
  }, [initialDraftReferences]);

  const composerEnabled = Boolean(conversation) || awaitingStart;
  const serverDraftReferenceCount = conversation?.draftReferences?.length ?? 0;
  const draftReferenceCount = serverDraftReferenceCount + localDraftReferences.length;
  const mention = useMemo(() => detectReferenceMention(body, caret), [body, caret]);
  const [debouncedMentionQuery] = useDebouncedValue(mention?.query ?? '', 220);
  const mentionOpen = Boolean(mention && (onAddReference || awaitingStart) && token);

  const mentionSelectable = useMemo(
    () => getSelectableMentionEntries(mention?.query ?? '', mentionSuggestions),
    [mention?.query, mentionSuggestions],
  );

  useEffect(() => {
    if (!mentionOpen || !token) {
      setMentionSuggestions([]);
      setMentionLoading(false);
      return;
    }
    const controller = new AbortController();
    setMentionLoading(true);
    void fetchMessageReferenceSuggestions(debouncedMentionQuery, token, controller.signal)
      .then((items: Awaited<ReturnType<typeof fetchMessageReferenceSuggestions>>) => {
        setMentionSuggestions(items);
        setMentionActiveIndex(0);
      })
      .catch(() => {
        if (!controller.signal.aborted) setMentionSuggestions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setMentionLoading(false);
      });
    return () => controller.abort();
  }, [debouncedMentionQuery, mentionOpen, token]);

  const canSend =
    Boolean(body.trim() || files.length > 0 || draftReferenceCount > 0) &&
    composerEnabled &&
    !loading &&
    !pending;

  const messageGroups = useMemo(() => {
    const groups: Array<{
      id: string;
      tone: 'inbound' | 'outbound' | 'internal';
      label: string;
      messages: ConversationMessageItem[];
    }> = [];

    for (const message of conversation?.messages ?? []) {
      const tone = messageTone(message, viewerUserId);
      const label = messageLabel(message, viewerUserId);
      const prev = groups.at(-1);
      const prevTime = prev ? new Date(prev.messages.at(-1)?.createdAt ?? 0).getTime() : 0;
      const nextTime = new Date(message.createdAt).getTime();
      const canJoin =
        prev &&
        prev.tone === tone &&
        prev.label === label &&
        nextTime - prevTime <= GROUP_GAP_MS;

      if (canJoin) {
        prev.messages.push(message);
        continue;
      }

      groups.push({
        id: message.id,
        tone,
        label,
        messages: [message],
      });
    }

    return groups;
  }, [conversation?.messages, viewerUserId]);

  useLayoutEffect(() => {
    const node = threadScrollRef.current;
    if (!node) return;
    const scroll = () => {
      node.scrollTop = node.scrollHeight;
    };
    scroll();
    requestAnimationFrame(() => {
      scroll();
      requestAnimationFrame(scroll);
    });
  }, [messageGroups, conversation?.id, loading, pending]);

  useEffect(() => {
    const content = messagesRef.current;
    if (!content) return;
    const scroll = () => {
      const node = threadScrollRef.current;
      if (!node) return;
      node.scrollTop = node.scrollHeight;
    };
    const observer = new ResizeObserver(() => scroll());
    observer.observe(content);
    return () => observer.disconnect();
  }, [conversation?.id]);

  const addFiles = (incoming: File[]) => {
    const next = appendSupportImageFiles(files, incoming);
    const check = validateSupportImageFileList(next);
    if (!check.ok) {
      showToast(check.reason, { tone: 'error' });
      return;
    }
    setFiles(next);
  };

  const removeFileAt = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
  };

  const insertEmoji = (emoji: string) => {
    setBody((current) => {
      const el = composerTextareaRef.current;
      const start = el?.selectionStart ?? current.length;
      const end = el?.selectionEnd ?? current.length;
      const next = current.slice(0, start) + emoji + current.slice(end);
      const nextCaret = start + [...emoji].length;
      setCaret(nextCaret);
      queueMicrotask(() => {
        const node = composerTextareaRef.current;
        if (!node) return;
        node.focus();
        node.setSelectionRange(nextCaret, nextCaret);
      });
      return next;
    });
  };

  const applyMentionEntityType = (refType: string) => {
    if (!mention) return;
    const kind = getMessageReferenceEntityKind(refType);
    const prefix = `@${kind?.aliases[0] ?? refType.toLowerCase()} `;
    const before = body.slice(0, mention.start);
    const after = body.slice(caret);
    const nextBody = `${before}${prefix}${after}`;
    const nextCaret = before.length + prefix.length;
    setBody(nextBody);
    setCaret(nextCaret);
    setMentionActiveIndex(0);
    queueMicrotask(() => {
      const node = composerTextareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const applyMentionResult = (item: MessageReferenceSuggestion) => {
    if (!mention) return;
    const before = body.slice(0, mention.start);
    const after = body.slice(caret);
    const nextBody = `${before}${after}`;
    const nextCaret = mention.start;
    setBody(nextBody);
    setCaret(nextCaret);
    queueMicrotask(() => {
      const node = composerTextareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(nextCaret, nextCaret);
    });
    if (conversation?.id && onAddReference) {
      void onAddReference({ refType: item.refType, refId: item.refId, label: item.label }).catch(
        (e: unknown) => {
          showToast(getDisplayError(e), { tone: 'error' });
        },
      );
      return;
    }
    setLocalDraftReferences((current) => {
      const key = localDraftReferenceKey(item.refType, item.refId);
      if (current.some((entry) => entry.key === key)) return current;
      return [
        ...current,
        {
          key,
          refType: item.refType,
          refId: item.refId,
          label: item.label?.trim() || null,
        },
      ];
    });
  };

  const removeLocalDraftReference = (key: string) => {
    setLocalDraftReferences((current) => current.filter((entry) => entry.key !== key));
  };

  const removeDraftReference = (referenceId: string) => {
    if (!onRemoveReference || removingReferenceId) return;
    setRemovingReferenceId(referenceId);
    void onRemoveReference(referenceId)
      .catch((e: unknown) => showToast(getDisplayError(e), { tone: 'error' }))
      .finally(() => setRemovingReferenceId(null));
  };

  const send = async () => {
    const next = body.trim();
    if ((!next && files.length === 0 && draftReferenceCount === 0) || pending || !composerEnabled) return;
    setPending(true);
    try {
      await onSend({
        body: next,
        isInternal: false,
        files,
        references: localDraftReferences.map((reference) => ({
          refType: reference.refType,
          refId: reference.refId,
          label: reference.label,
        })),
      });
      setBody('');
      setFiles([]);
      setLocalDraftReferences([]);
    } catch (e: unknown) {
      showToast(getDisplayError(e), { tone: 'error' });
    } finally {
      setPending(false);
    }
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen) {
      if (event.key === 'ArrowDown' && mentionSelectable.length) {
        event.preventDefault();
        setMentionActiveIndex((current) => Math.min(current + 1, mentionSelectable.length - 1));
        return;
      }
      if (event.key === 'ArrowUp' && mentionSelectable.length) {
        event.preventDefault();
        setMentionActiveIndex((current) => Math.max(current - 1, 0));
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey && mentionSelectable.length) {
        event.preventDefault();
        const picked = mentionSelectable[mentionActiveIndex] ?? mentionSelectable[0]!;
        if (picked.kind === 'entity-type') {
          applyMentionEntityType(picked.refType);
          return;
        }
        applyMentionResult(picked);
        return;
      }
      if (event.key === 'Escape' && mention) {
        event.preventDefault();
        const nextBody = body.slice(0, mention.start) + body.slice(caret);
        setBody(nextBody);
        setCaret(mention.start);
        queueMicrotask(() => {
          const node = composerTextareaRef.current;
          if (!node) return;
          node.focus();
          node.setSelectionRange(mention.start, mention.start);
        });
        return;
      }
    }
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (!canSend) return;
    void send();
  };

  const onPickFiles = (picked: FileList | null) => {
    if (!picked?.length) return;
    addFiles(Array.from(picked));
  };

  const onPaste = async () => {
    setBusyAction('paste');
    try {
      const file = await readClipboardImage();
      if (!file) {
        showToast('Aucune image dans le presse-papiers.', { tone: 'error' });
        return;
      }
      addFiles([file]);
    } finally {
      setBusyAction(null);
    }
  };

  const onPasteEvent = (event: ClipboardEvent<HTMLDivElement>) => {
    const file = readClipboardImageFromEvent(event.nativeEvent);
    if (!file) return;
    event.preventDefault();
    addFiles([file]);
  };

  const onCaptureScreen = async () => {
    setBusyAction('screen');
    try {
      const file = await captureScreen();
      if (!file) {
        showToast('Capture écran annulée ou indisponible.', { tone: 'error' });
        return;
      }
      addFiles([file]);
    } finally {
      setBusyAction(null);
    }
  };

  const onCapturePage = async () => {
    setBusyAction('page');
    try {
      const file = await captureVisiblePage();
      if (!file) {
        showToast('Capture page impossible.', { tone: 'error' });
        return;
      }
      addFiles([file]);
    } finally {
      setBusyAction(null);
    }
  };

  const attachmentsLabel =
    files.length === 0
      ? 'Aucune pièce jointe'
      : `${files.length}/${SUPPORT_ATTACHMENT_MAX_COUNT} image${files.length > 1 ? 's' : ''}`;

  return (
    <div className={`${styles.shell} ${compact ? styles.compact : ''}`}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          {onBack ? (
            <IconButton type="button" variant="ghost" size="sm" aria-label="Retour" onClick={onBack}>
              <IconArrowLeft {...WP_ICON_NAV} />
            </IconButton>
          ) : null}
          <Avatar size="md" label={title} src={avatarUrl} />
          <div className={styles.identity}>
            <Text size="sm" fw={700} className={styles.title}>
              {title}
            </Text>
            {(entityType ?? conversation?.entityType) ? (
              <EntityTypeMark entityType={entityType ?? conversation?.entityType} />
            ) : null}
            {subtitle ? (
              <Text size="xs" c="dimmed" className={styles.subtitle}>
                {subtitle}
              </Text>
            ) : null}
          </div>
        </div>
        <div className={styles.headerActions}>
          {onOpenContext ? (
            <IconButton type="button" variant="ghost" size="sm" aria-label="Voir contexte" onClick={onOpenContext}>
              <IconArrowUpRight {...WP_ICON_NAV} />
            </IconButton>
          ) : null}
        </div>
      </div>

      <div className={styles.threadSurface}>
        <ScrollArea
          ref={threadScrollRef}
          className={styles.threadScroll}
          viewportClassName={styles.threadScrollViewport}
          fade="y"
          maxHeight={compact ? '100%' : undefined}
          style={compact ? { height: '100%' } : undefined}
        >
          {loading && !conversation ? <Loader size="sm" /> : null}
          {!loading && !(conversation?.messages.length ?? 0) ? (
            <div className={styles.empty}>
              <EmptyState density="compact" title="Aucun message pour l'instant." />
            </div>
          ) : (
            <div className={styles.messages} ref={messagesRef}>
              {messageGroups.map((group) => {
                const tone = group.tone;
                return (
                  <div
                    key={group.id}
                    className={`${styles.messageRow} ${
                      tone === 'inbound'
                        ? styles.rowInbound
                        : tone === 'outbound'
                          ? styles.rowOutbound
                          : styles.rowInternal
                    }`}
                  >
                    <div className={styles.messageBlock}>
                      <div className={styles.bubbleStack}>
                        {group.messages.map((message) => {
                          const hasAttachments = (message.attachments?.length ?? 0) > 0;
                          return (
                          <div
                            key={message.id}
                            className={`${styles.bubble} ${
                              tone === 'inbound'
                                ? styles.bubbleInbound
                                : tone === 'outbound'
                                  ? styles.bubbleOutbound
                                  : styles.bubbleInternal
                            }${hasAttachments ? ` ${styles.bubbleWithAttachments}` : ''}`}
                          >
                            {(message.references?.length ?? 0) > 0 ? (
                              <Stack gap="xs" style={{ marginBottom: message.body.trim() || hasAttachments ? 8 : 0 }}>
                                {(message.references ?? []).map((reference) => {
                                  const snapshot = reference.snapshot ?? {};
                                  const title =
                                    (typeof snapshot.title === 'string' && snapshot.title.trim()) ||
                                    reference.label?.trim() ||
                                    getEntityLabel(reference.refType, reference.refId);
                                  const subtitle =
                                    typeof snapshot.subtitle === 'string' && snapshot.subtitle.trim()
                                      ? snapshot.subtitle
                                      : null;
                                  const imageUrl =
                                    typeof snapshot.imageUrl === 'string' && snapshot.imageUrl.trim()
                                      ? snapshot.imageUrl
                                      : null;
                                  return (
                                    <EntityReferenceChip
                                      key={reference.id}
                                      entityType={reference.refType}
                                      entityId={reference.refId}
                                      title={title}
                                      subtitle={subtitle}
                                      imageUrl={imageUrl}
                                      href={getEntityHref(reference.refType, reference.refId)}
                                    />
                                  );
                                })}
                              </Stack>
                            ) : null}
                            {message.body.trim() ? <Text size="sm">{message.body}</Text> : null}
                            {hasAttachments ? (
                              <div className={styles.attachments}>
                                <ImageAttachmentGrid attachments={message.attachments ?? []} compact />
                              </div>
                            ) : null}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className={styles.scrollAnchor} aria-hidden />
            </div>
          )}
        </ScrollArea>
      </div>

      <div className={styles.composer} onPaste={onPasteEvent}>
        <Stack gap="sm">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/avif,.avif"
            multiple
            hidden
            onChange={(e) => {
              onPickFiles(e.currentTarget.files);
              e.currentTarget.value = '';
            }}
          />
          <div className={styles.inputShellWrap}>
            <MessageReferenceMentionMenu
              open={mentionOpen}
              loading={mentionLoading}
              query={mention?.query ?? ''}
              items={mentionSuggestions}
              activeIndex={mentionActiveIndex}
              onPickEntityType={applyMentionEntityType}
              onPickResult={applyMentionResult}
              onHover={setMentionActiveIndex}
            />
            <div className={`${styles.inputShell} ${mentionOpen ? styles.inputShellMentionOpen : ''}`}>
            {(conversation?.draftReferences?.length ?? 0) > 0 || localDraftReferences.length > 0 ? (
              <div className={styles.composerAttachments}>
                <Stack gap="xs">
                  {(conversation?.draftReferences ?? []).map((reference) => {
                    const snapshot = reference.snapshot ?? {};
                    const refTitle =
                      (typeof snapshot.title === 'string' && snapshot.title.trim()) ||
                      reference.label?.trim() ||
                      getEntityLabel(reference.refType, reference.refId);
                    const refSubtitle =
                      typeof snapshot.subtitle === 'string' && snapshot.subtitle.trim()
                        ? snapshot.subtitle
                        : null;
                    const refImageUrl =
                      typeof snapshot.imageUrl === 'string' && snapshot.imageUrl.trim()
                        ? snapshot.imageUrl
                        : null;
                    return (
                      <EntityReferenceChip
                        key={reference.id}
                        entityType={reference.refType}
                        entityId={reference.refId}
                        title={refTitle}
                        subtitle={refSubtitle}
                        imageUrl={refImageUrl}
                        href={getEntityHref(reference.refType, reference.refId)}
                        disabled={pending || removingReferenceId === reference.id}
                        onRemove={() => removeDraftReference(reference.id)}
                      />
                    );
                  })}
                  {localDraftReferences.map((reference) => (
                    <EntityReferenceChip
                      key={reference.key}
                      entityType={reference.refType}
                      entityId={reference.refId}
                      title={reference.label ?? getEntityLabel(reference.refType, reference.refId)}
                      href={getEntityHref(reference.refType, reference.refId)}
                      disabled={pending}
                      onRemove={() => removeLocalDraftReference(reference.key)}
                    />
                  ))}
                </Stack>
              </div>
            ) : null}
            {files.length > 0 ? (
              <div className={styles.composerAttachments}>
                <ImageAttachmentGrid
                  attachments={previews}
                  embedded
                  onRemove={removeFileAt}
                />
              </div>
            ) : null}
            <TextArea
              ref={composerTextareaRef}
              className={`${styles.composerInput}${files.length > 0 || draftReferenceCount > 0 ? ` ${styles.composerInputWithAttachments}` : ''}`}
              value={body}
              onChange={(e) => {
                setBody(e.currentTarget.value);
                setCaret(e.currentTarget.selectionStart ?? 0);
              }}
              onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
              onKeyDown={onComposerKeyDown}
              disabled={!composerEnabled || loading || pending}
              placeholder="Écrire un message… (@ pour référencer)"
              showErrorText={false}
            />
            <div className={styles.inputDockRow}>
              <div className={styles.dock}>
                <EmojiPicker
                  disabled={!composerEnabled || loading || pending}
                  align="start"
                  onSelect={insertEmoji}
                />
                <Tooltip label="Ajouter une image" portal position="top">
                  <IconButton
                    aria-label="Ajouter une image"
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={pending}
                  >
                    <IconPaperclip size={16} stroke={1.8} />
                  </IconButton>
                </Tooltip>
                <Tooltip label="Coller une image" portal position="top">
                  <IconButton
                    aria-label="Coller une image"
                    variant="ghost"
                    size="sm"
                    onClick={() => void onPaste()}
                    disabled={pending || busyAction !== null}
                  >
                    <IconClipboard size={16} stroke={1.8} />
                  </IconButton>
                </Tooltip>
                <Tooltip label="Capturer l'écran" portal position="top">
                  <IconButton
                    aria-label="Capturer l'écran"
                    variant="ghost"
                    size="sm"
                    onClick={() => void onCaptureScreen()}
                    disabled={pending || busyAction !== null}
                  >
                    <IconDeviceDesktop size={16} stroke={1.8} />
                  </IconButton>
                </Tooltip>
                <Tooltip label="Capturer la page" portal position="top">
                  <IconButton
                    aria-label="Capturer la page"
                    variant="ghost"
                    size="sm"
                    onClick={() => void onCapturePage()}
                    disabled={pending || busyAction !== null}
                  >
                    <IconPhoto size={16} stroke={1.8} />
                  </IconButton>
                </Tooltip>
              </div>
              <Text size="xs" c="dimmed" className={styles.attachmentsLabel}>
                {attachmentsLabel}
              </Text>
              <Tooltip label={pending ? 'Envoi…' : 'Envoyer'} portal position="top">
                <IconButton
                  type="button"
                  variant="primary"
                  aria-label="Envoyer"
                  className={styles.sendButton}
                  disabled={!canSend}
                  onClick={() => void send()}
                >
                  <IconSend2 size={18} stroke={1.8} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          </div>
        </Stack>
      </div>
    </div>
  );
}
